import { io } from 'socket.io-client';
import { getToken } from './authService';

let socket = null;

/**
 * CONNECT to Socket.IO server with authentication
 * @param {Function} onAuthenticated - Callback when authenticated
 * @param {Function} onError - Callback for errors
 * @returns {Socket} Socket.IO client instance
 */
export function connect(onAuthenticated, onError) {
  const token = getToken();

  if (!token) {
    if (onError) onError(new Error('No authentication token found'));
    return null;
  }

  // CREATE Socket.IO connection (AC2.1, AC2.3 - auto-reconnection)
  socket = io(process.env.REACT_APP_WS_URL, {
    reconnection: true,
    reconnectionDelay: 1000, // Start with 1s
    reconnectionDelayMax: 30000, // Max 30s
    reconnectionAttempts: Infinity,
    transports: ['websocket', 'polling']
  });

  // CONNECTION event
  socket.on('connect', () => {
    console.log('Connected to WebSocket server', socket.id);

    // AUTHENTICATE with server
    socket.emit('authenticate', { token });
  });

  // AUTHENTICATED event (AC2.1 success)
  socket.on('authenticated', (data) => {
    console.log('Authenticated successfully', data.user);
    if (onAuthenticated) onAuthenticated(data);
  });

  // ERROR event (AC2.7)
  socket.on('error', (error) => {
    console.error('Socket error:', error);
    if (onError) onError(error);
  });

  // DISCONNECT event
  socket.on('disconnect', (reason) => {
    console.log('Disconnected from server:', reason);
  });

  // RECONNECT attempt (AC2.3)
  socket.io.on('reconnect_attempt', (attemptNumber) => {
    console.log(`Reconnection attempt ${attemptNumber}`);
  });

  // RECONNECT event
  socket.on('reconnect', (attemptNumber) => {
    console.log(`Reconnected after ${attemptNumber} attempts`);

    // RE-AUTHENTICATE after reconnection
    const freshToken = getToken();
    if (freshToken) {
      socket.emit('authenticate', { token: freshToken });
    }
  });

  return socket;
}

/**
 * DISCONNECT from Socket.IO server
 */
export function disconnect() {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

/**
 * JOIN a channel (AC3.4, AC3.5)
 * @param {string} channelName - Channel name
 * @param {Function} onJoined - Callback when joined
 */
export function joinChannel(channelName, onJoined) {
  if (!socket || !socket.connected) {
    return;
  }

  socket.emit('join-channel', { channelName });

  if (onJoined) {
    socket.once('channel-joined', onJoined);
  }
}

/**
 * LEAVE a channel
 * @param {string} channelName - Channel name
 */
export function leaveChannel(channelName) {
  if (!socket || !socket.connected) {
    return;
  }

  socket.emit('leave-channel', { channelName });
}

/**
 * LISTEN for channel-joined event (AC3.2)
 * @param {Function} callback - Callback when channel joined
 */
export function onChannelJoined(callback) {
  if (socket) {
    socket.on('channel-joined', callback);
  }
}

/**
 * LISTEN for portal welcome message (AC3.7)
 * @param {Function} callback - Callback for welcome message
 */
export function onPortalWelcome(callback) {
  if (socket) {
    socket.on('portal-welcome', callback);
  }
}

/**
 * SEND message to server (AC2.4) - Updated for Story 3
 * @param {string} channelName - Channel name
 * @param {string} message - Message content
 * @param {Function} onError - Error callback
 */
export function sendMessage(channelName, message, onError) {
  if (!socket || !socket.connected) {
    if (onError) onError(new Error('Not connected to server'));
    return;
  }

  socket.emit('send-message', { channelName, message });
}

/**
 * LISTEN for incoming messages (AC2.5)
 * @param {Function} callback - Callback when message received
 */
export function onMessage(callback) {
  if (socket) {
    socket.on('message', callback);
  }
}

/**
 * REMOVE message listener
 */
export function offMessage() {
  if (socket) {
    socket.off('message');
  }
}

/**
 * GET current socket instance
 * @returns {Socket|null}
 */
export function getSocket() {
  return socket;
}

/**
 * CHECK if connected
 * @returns {boolean}
 */
export function isConnected() {
  return socket && socket.connected;
}

/**
 * SEND command to server (Story 5)
 * @param {string} message - Command message (e.g., '/nick d0dger')
 * @param {string} currentChannel - Current channel name
 * @param {Function} onError - Error callback
 */
export function sendCommand(message, currentChannel, onError) {
  if (!socket || !socket.connected) {
    if (onError) onError(new Error('Not connected to server'));
    return;
  }

  socket.emit('send-command', { message, currentChannel });
}

/**
 * LISTEN for command errors
 * @param {Function} callback - Callback when error received
 */
export function onCommandError(callback) {
  if (socket) {
    socket.on('command-error', callback);
  }
}

/**
 * LISTEN for nick changed event
 * @param {Function} callback - Callback when handle changed
 */
export function onNickChanged(callback) {
  if (socket) {
    socket.on('nick-changed', callback);
  }
}

/**
 * LISTEN for system messages
 * @param {Function} callback - Callback for system messages
 */
export function onSystemMessage(callback) {
  if (socket) {
    socket.on('system-message', callback);
  }
}

/**
 * LISTEN for help command
 * @param {Function} callback - Callback when /help triggered
 */
export function onShowHelp(callback) {
  if (socket) {
    socket.on('show-help', callback);
  }
}

/**
 * LISTEN for whisper messages (Story 6)
 * @param {Function} callback - Callback when whisper received
 */
export function onWhisper(callback) {
  if (socket) {
    socket.on('whisper', callback);
  }
}

/**
 * LISTEN for user list updates (Story 7)
 * @param {Function} callback - Callback when user list changes
 */
export function onUserListUpdate(callback) {
  if (socket) {
    socket.on('user-list-update', callback);
  }
}

/**
 * LISTEN for message deletion events (Story 10)
 * @param {Function} callback - Callback when message is deleted by admin
 */
export function onMessageDeleted(callback) {
  if (socket) {
    socket.on('message-deleted', callback);
  }
}
